import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import type { LegacyReaderHandle } from '../../legacy/legacyGlobals';
import { unmountExtractedScreen } from '../../legacy/screenHost';
import type { PageVerse } from '../../shared/quran/pageStructure';
import type { LaidOutWord } from '../../shared/quran/wordAudio';
import type { ReadingPreferences } from '../../shared/state/readingPreferences';
import type { ReaderHost, VerseRowModel } from './readerHost';
import { useWordAudio } from './useWordAudio';
import { RecitationPlayer, RecitationPrompt } from './RecitationPlayer';
import { recitationQueue, surahBeginsOnScreen } from './recitationQueue';
import { BasmalaRow, SurahBand, VerseRow, type RowState } from './rows';

const asHtml = (html: string) => ({ __html: html });

const GUIDE_IC = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="2.8"/></svg>';

type PageItem =
  | { kind: 'band'; surah: number }
  | { kind: 'basmala' }
  | { kind: 'verse'; verse: VerseRowModel };

interface BuiltPage {
  items: PageItem[];
  verses: PageVerse[];
  models: Map<string, VerseRowModel>;
  peek: VerseRowModel[];
  /** Each verse's laid-out word locations, for word audio and timings. */
  locs: Map<string, string[]>;
}

/**
 * Every word of the verse as its own span, so it can be tapped to hear it and
 * lit as the reciter reaches it; the page's tajweed font wraps the whole verse
 * exactly as the suite's plain text did.
 */
function wordSpans(locs: string[], words: string[], prefs: ReadingPreferences, page: number): string {
  const inner = words.map((word, i) => '<span class="aqw" data-loc="' + locs[i] + '">' + word + '</span>').join(' ');
  return prefs.script === 'uthmani' && prefs.tajweed ? '<span style="font-family:\'v4p' + page + '\'">' + inner + '</span>' : inner;
}

/**
 * The page's rows in mushaf order (legacy itemsFor), then the first four
 * verses of the next page for the blurred taste at the foot (legacy
 * renderFinishBox) — in that order, because the host registers each verse
 * as it goes and the later registration wins.
 */
function buildPage(host: ReaderHost, page: number): BuiltPage {
  host.beginPage();
  const prefs = host.prefs();
  const items: PageItem[] = [];
  const models = new Map<string, VerseRowModel>();
  const locs = new Map<string, string[]>();
  const row = (verse: PageVerse, p: number) => {
    const model = host.verseRow(verse, p);
    const words = host.arabicWords(verse, p);
    if (words) model.ar = wordSpans(verse.locs, words, prefs, p);
    locs.set(verse.key, verse.locs);
    return model;
  };
  const verses = host.versesOn(page);
  for (const verse of verses) {
    if (verse.surahBefore) items.push({ kind: 'band', surah: verse.surahBefore });
    if (verse.basmalaBefore) items.push({ kind: 'basmala' });
    const model = row(verse, page);
    models.set(verse.key, model);
    items.push({ kind: 'verse', verse: model });
  }
  const next = page + 1;
  const peek = next <= host.maxPage() ? host.versesOn(next).slice(0, 4).map((v) => row(v, next)) : [];
  return { items, verses, models, peek, locs };
}

interface ReaderScreenProps {
  host: ReaderHost;
  page: number;
  onReady?: (handle: LegacyReaderHandle) => void;
}

/**
 * SCREEN 4 — the verse-by-verse reader (legacy openReader + wireReader).
 * Verses pass a read line 10% down the reading area and become "pending";
 * the header chip is a thermometer that fills as the page is read and reads
 * "Finished" once every verse has passed; the finish box at the foot submits
 * the page or takes another one. The DOM matches the legacy screen class for
 * class; the recitation player, the settings sheet and the search overlay
 * remain legacy and mount into this screen as foreign nodes.
 */
export function ReaderScreen({ host, page: initialPage, onReady }: ReaderScreenProps) {
  const t = host.t;
  const icons = useMemo(() => host.icons(), [host]);
  const prefs = host.prefs();

  const [page, setPage] = useState(initialPage);
  const built = useMemo(() => buildPage(host, page), [host, page]);
  const [, rerender] = useState(0);
  /* rows already confirmed on an earlier visit start out ticked, as buildScreen painted them */
  const initialRowStates = (verses: PageVerse[]) => new Map<string, RowState>(verses.map((v) => [v.key, host.confirmed().has(v.key) ? 'confirmed' : null]));
  const [rowStates, setRowStates] = useState<Map<string, RowState>>(() => initialRowStates(built.verses));
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [barPage, setBarPage] = useState(initialPage);
  const [header, setHeader] = useState(() => ({
    title: host.surahTitle(built.verses[0].key),
    sub: 'Page ' + initialPage + ', Juzuʼ ' + host.juzOf(initialPage),
  }));
  const [therm, setTherm] = useState({ done: 0, total: 0, pct: 0 });
  const [chipsTick, setChipsTick] = useState(0);
  const [hideGuides, setHideGuides] = useState(() => document.documentElement.classList.contains('qc-hideguides'));
  const [readLineTop, setReadLineTop] = useState<number | null>(null);
  /* the bottom bar's recitation: first the "new surah on screen" prompt, then the player over the bar */
  const [recitePrompt, setRecitePrompt] = useState(false);
  const [reciteQueue, setReciteQueue] = useState<string[] | null>(null);

  const screenRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLButtonElement>(null);
  const settingsRef = useRef<HTMLButtonElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const finishRef = useRef<HTMLDivElement>(null);
  const peekRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);

  const laidOut = (verseKey: string): LaidOutWord[] => (built.locs.get(verseKey) ?? []).map((loc) => ({ loc, text: host.hafsText(loc) }));
  const { playWord, follow } = useWordAudio(host, contentRef, laidOut);

  const pending = useRef(new Set<string>());
  const undoing = useRef(new Set<string>());
  /** pages fully read this sitting but already replaced on screen */
  const sitting = useRef<number[]>([]);
  const appending = useRef(false);
  const wasComplete = useRef(false);
  const pageJustChanged = useRef(false);
  const pageRef = useRef(page);

  /* ---- geometry (legacy lineY / linePoint / chipRect) ---- */
  const rows = () => Array.from(contentRef.current!.querySelectorAll<HTMLElement>(':scope > .vrow[data-id]'));
  const lineY = () => { const r = scrollRef.current!.getBoundingClientRect(); return r.top + r.height * 0.10; };
  const linePoint = () => { const r = scrollRef.current!.getBoundingClientRect(); return { left: r.left + 6, top: r.top + r.height * 0.10 - 13, width: 36, height: 26 }; };
  const chipRect = (): { left: number; top: number; width: number; height: number } => {
    const b = counterRef.current!.getBoundingClientRect(); if (b.width > 0) return b;
    const h = screenRef.current!.getBoundingClientRect(); return { left: h.right - 90, top: h.top + 18, width: 36, height: 26 };
  };

  const popCounter = () => { const c = counterRef.current!; c.classList.remove('pop'); void c.offsetWidth; c.classList.add('pop'); };

  /* the book-bird that carries a verse up to the counter (and back when a verse is un-read) */
  const flyBird = (fromRect: { left: number; top: number; width: number; height: number }, toRect: { left: number; top: number; width: number; height: number }, minus: boolean) => {
    if (host.reduceMotion()) return;
    const screen = screenRef.current!;
    const f = document.createElement('span');
    f.className = 'flyer' + (minus ? ' ghost' : '');
    f.innerHTML = icons.birdFlyer;
    const ph = screen.getBoundingClientRect();
    f.style.left = (fromRect.left - ph.left + fromRect.width / 2 - 24) + 'px';
    f.style.top = (fromRect.top - ph.top + fromRect.height / 2 - 18) + 'px';
    screen.appendChild(f);
    f.classList.add(minus ? 'flying' : 'opening');
    if (!minus) setTimeout(() => { f.classList.remove('opening'); f.classList.add('flying'); }, 140);
    const dx = (toRect.left + toRect.width / 2) - (fromRect.left + fromRect.width / 2);
    const dy = (toRect.top + toRect.height / 2) - (fromRect.top + fromRect.height / 2);
    f.animate([
      { transform: 'translate(0,0) scale(' + (minus ? 0.55 : 0.9) + ') rotate(0deg)', opacity: 1 },
      { transform: 'translate(' + (dx * 0.5) + 'px,' + (dy * 0.5 - 36) + 'px) scale(' + (minus ? 0.9 : 1.1) + ') rotate(' + (minus ? 10 : -10) + 'deg)', opacity: 1, offset: .55 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + (minus ? 0.95 : 0.5) + ') rotate(0deg)', opacity: (minus ? 0.9 : 0.2) },
    ], { duration: minus ? 620 : 780, easing: 'cubic-bezier(.35,.6,.35,1)' }).onfinish = () => {
      if (minus) {
        const wl = f.querySelector<HTMLElement>('.wL'), wr = f.querySelector<HTMLElement>('.wR');
        f.classList.remove('flying');
        if (wl) wl.style.animation = 'openL .22s ease-in reverse both';
        if (wr) wr.style.animation = 'openR .22s ease-in reverse both';
        setTimeout(() => f.remove(), 240);
      } else { f.remove(); popCounter(); }
    };
  };

  /* ---- progress (legacy readOn / pageComplete / updateChips) ---- */
  const readOn = (p: number) => host.versesOn(p).filter((v) => pending.current.has(v.key) || host.confirmed().has(v.key)).length;
  const pageComplete = (p: number) => readOn(p) >= host.versesOn(p).length;
  const updateChips = () => {
    const p = pageRef.current;
    const done = readOn(p), total = host.versesOn(p).length;
    setTherm({ done, total, pct: Math.round(done / total * 100) });
    setChipsTick((n) => n + 1);
  };

  const rowStateOf = (id: string): RowState => {
    if (undoing.current.has(id)) return 'undoing';
    if (host.confirmed().has(id)) return 'confirmed';
    if (pending.current.has(id)) return 'counted';
    return null;
  };

  /* the read line: verses whose bottom edge has passed it are pending; confirmed verses
     scrolled back below it are flagged for undo (legacy detect) */
  const detect = () => {
    if (!contentRef.current || !scrollRef.current) return;
    const ly = lineY();
    const confirmed = host.confirmed();
    rows().forEach((row) => {
      const id = row.dataset.id!;
      const r = row.getBoundingClientRect();
      const above = r.bottom <= ly;
      if (confirmed.has(id)) {
        const flagged = undoing.current.has(id);
        if (!flagged && r.top >= ly) { undoing.current.add(id); updateChips(); flyBird(chipRect(), linePoint(), true); }
        else if (flagged && r.bottom <= ly) { undoing.current.delete(id); updateChips(); }
        return;
      }
      const was = pending.current.has(id);
      if (above && !was) { pending.current.add(id); flyBird(linePoint(), chipRect(), false); updateChips(); }
      else if (!above && was) { pending.current.delete(id); flyBird(chipRect(), linePoint(), true); updateChips(); }
    });
    const states = new Map<string, RowState>();
    for (const v of built.verses) states.set(v.key, rowStateOf(v.key));
    setRowStates((prev) => {
      for (const [id, s] of states) if (prev.get(id) !== s) return states;
      return prev.size === states.size ? prev : states;
    });
    let cur: HTMLElement | null = null;
    rows().forEach((row) => { const r = row.getBoundingClientRect(); if (cur === null && r.bottom > ly) cur = row; });
    const curRow = cur as HTMLElement | null;
    setCurrentId(curRow ? curRow.dataset.id! : null);
    setBarPage(pageRef.current);
    /* the header follows the verse at the read line (legacy updateHeader) */
    const v = curRow && built.models.get(curRow.dataset.id!);
    if (v) {
      setHeader({ title: v.surahName, sub: 'Page ' + v.page + ', Juzuʼ ' + v.juz });
      host.setCurrentPage(v.page); host.persist();
    }
    /* always refresh: the thermometer also has to reset when a new page is loaded */
    updateChips();
  };
  const detectRef = useRef(detect);
  /* the scroll listener and timers always run the latest closure over the current page */
  useLayoutEffect(() => { pageRef.current = page; detectRef.current = detect; });

  const toast = (msg: string, ms = 1900) => {
    const screen = screenRef.current!;
    let el = screen.querySelector<HTMLElement & { _h?: number }>('.toast');
    if (!el) { el = document.createElement('div'); el.className = 'toast'; screen.appendChild(el); }
    el.textContent = msg; el.classList.add('show');
    clearTimeout(el._h); el._h = window.setTimeout(() => el!.classList.remove('show'), ms);
  };
  const closeOverlays = () => { screenRef.current!.querySelectorAll('.dim,.sheet,.dialog,.kscreen,.spot,.wcard').forEach((x) => x.remove()); };

  /* ---- recitation (legacy audioStart / audioPrompt / audioOpen) ---- */
  const pageOf = (id: string) => built.models.get(id)?.page ?? built.peek.find((v) => v.id === id)?.page;
  const openPlayer = (fromSurah: boolean) => {
    setRecitePrompt(false);
    if (!host.audio().enabled()) { toast('Recitation plays in the live app — it is switched off in this shared preview', 3200); return; }
    const queue = recitationQueue(scrollRef.current!, pageOf, fromSurah);
    if (queue.length) setReciteQueue(queue);
  };
  const startRecitation = () => {
    if (reciteQueue || recitePrompt) return;
    if (surahBeginsOnScreen(scrollRef.current!)) setRecitePrompt(true);
    else openPlayer(false);
  };
  /* the reciting verse is highlighted and kept in view, and its words light up in time;
     the scroll feeds the read line as any scroll does */
  const onReciteVerse = (id: string | null) => {
    setPlayingId(id);
    follow(id);
    if (!id) return;
    const row = contentRef.current?.querySelector<HTMLElement>('.vrow[data-id="' + id + '"]');
    if (row) row.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const handle = (): LegacyReaderHandle => ({
    screen: screenRef.current!, scroll: scrollRef.current!, content: contentRef.current!, counter: counterRef.current!,
    bar: barRef.current!, med: barRef.current!.querySelector('.medallion span'), settingsBtn: settingsRef.current!, undochip: null,
    _appendPage: appendPage,
  });

  /* every page fully read this sitting goes in together, including ones already replaced on screen */
  const submitAndContinue = () => {
    const cur = pageRef.current;
    const pages = sitting.current.slice();
    if (pageComplete(cur) && !pages.includes(cur)) pages.push(cur);
    unmountExtractedScreen();
    host.finishedReading(pages.length ? pages : [cur]);
  };

  /* rapid page-flip, then the next page replaces this one — only ever one page on screen */
  async function appendPage() {
    if (appending.current) return;
    const np = pageRef.current + 1;
    const maxP = host.maxPage();
    if (np > maxP) {
      toast(maxP < 604 ? 'End of this preview — the live app continues to page 604' : 'That was the last page — khatam!');
      return;
    }
    appending.current = true;
    try {
      const p = host.prefs();
      if (p.script === 'uthmani' && p.tajweed) await host.loadPageFont(np);
      if (pageComplete(pageRef.current) && !sitting.current.includes(pageRef.current)) sitting.current.push(pageRef.current);
      await host.flipPages(screenRef.current!);
      host.setCurrentPage(np); host.persist();
      wasComplete.current = false;
      pageJustChanged.current = true;
      pageRef.current = np;   /* a scroll before the next commit must already measure the new page */
      setPage(np);
      setBarPage(np);
      setRowStates(initialRowStates(host.versesOn(np)));
      setCurrentId(null);
    } finally { appending.current = false; }
  }

  const readMore = async () => {
    if (!pageComplete(pageRef.current)) { toast(t('toastBottom')); return; }
    if (host.signedIn()) {
      /* already signed in — submit this page now and jump into a fresh backend-assigned page */
      const justRead = pageRef.current;
      host.donePages().add(justRead);
      host.streakPages().add(justRead);
      host.versesOn(justRead).forEach((v) => host.confirmed().add(v.key));
      host.persist();
      host.submitPages([justRead]);
      await host.goToNextPage(moreRef.current, () => host.openReader());
    } else {
      void appendPage();   /* not signed in — keep reading locally until the one registration gate */
    }
  };

  const submitOrNudge = () => { if (pageComplete(pageRef.current)) submitAndContinue(); else toast(t('toastBottom')); };

  /* tap any word, in any layout, to hear it (delegated, so it survives a page replacement) */
  const onContentClick = (e: MouseEvent<HTMLDivElement>) => {
    const tok = (e.target as Element).closest<HTMLElement>('.wtoken[data-loc], .aqw[data-loc]');
    if (tok) void playWord(tok.dataset.loc!, tok);
  };

  const toggleGuides = () => {
    const off = !hideGuides;
    setHideGuides(off);
    /* the live-source chip is mounted on <body>, so it is hidden from the document root */
    document.documentElement.classList.toggle('qc-hideguides', off);
  };

  /* the design's settings sheet, wired to the real options (legacy settings wiring) */
  const openSettings = () => {
    const ui = host.prefs();
    const o = host.settingsSheet({
      mode: ui.mode === 'full' ? 'full' : (ui.layout === 'arabic' ? 'arabic' : 'vbv'),
      layout: ui.layout === 'arabic' ? 'side' : ui.layout,
      translit: ui.translit, arSize: 'md', script: ui.script,
    });
    const screen = screenRef.current!;
    screen.appendChild(o.dim); screen.appendChild(o.sheet);
    const apply = () => { host.persist(); host.syncPrefs(); rerender((n) => n + 1); setTimeout(() => detectRef.current(), 0); };
    const layCards: [HTMLElement, 'side' | 'words' | 'stack'][] = [[o.side, 'side'], [o.words, 'words'], [o.stack, 'stack']];
    layCards.forEach(([card, val]) => card.addEventListener('click', async () => {
      ui.mode = 'vbv'; ui.layout = val;
      o.full.classList.remove('sel'); o.arabicOnly.classList.remove('sel');
      layCards.forEach(([c]) => c.classList.remove('sel'));
      card.classList.add('sel');
      if (val === 'words' && ui.script !== 'uthmani') { await host.ensureScriptData(); host.openReader(); return; }
      apply();
    }));
    o.full.addEventListener('click', () => { ui.mode = 'full'; host.persist(); host.syncPrefs(); closeOverlays(); host.openReader(); });
    o.arabicOnly.addEventListener('click', () => {
      [o.side, o.words, o.stack, o.full].forEach((c) => c.classList.remove('sel'));
      o.arabicOnly.classList.add('sel');
      ui.mode = 'vbv'; ui.layout = 'arabic'; apply();
    });
    o.sw.addEventListener('click', () => { ui.translit = !ui.translit; o.sw.classList.toggle('off', !ui.translit); apply(); });
    const segBtns: [HTMLElement, 'uthmani' | 'indopak' | 'turkish'][] = [[o.scU, 'uthmani'], [o.scI, 'indopak'], [o.scT, 'turkish']];
    segBtns.forEach(([b, val]) => {
      b.classList.toggle('sel', ui.script === val);
      b.addEventListener('click', () => { ui.script = val; host.normalizePrefs(); host.persist(); host.syncPrefs(); closeOverlays(); host.openReader(); });
    });
    o.tajwidSw.classList.toggle('off', !ui.tajweed);
    o.tajwidSw.addEventListener('click', () => { ui.tajweed = !ui.tajweed; host.persist(); host.syncPrefs(); closeOverlays(); host.openReader(); });
    o.arSlider.value = String(ui.arfs);
    o.arSlider.dispatchEvent(new Event('input'));
    o.arSlider.addEventListener('input', () => { ui.arfs = +o.arSlider.value; apply(); });
    o.enSlider.value = String(ui.enfs);
    o.enSlider.dispatchEvent(new Event('input'));
    o.enSlider.addEventListener('input', () => { ui.enfs = +o.enSlider.value; apply(); });
    /* replay the setup walkthrough */
    const replay = document.createElement('button');
    replay.className = 'btn outline'; replay.innerHTML = t('replayWalk');
    replay.style.cssText = 'width:100%; margin-top:8px;';
    replay.addEventListener('click', () => { closeOverlays(); unmountExtractedScreen(); host.startOnboarding(); });
    o.sheet.appendChild(replay);
    o.done.addEventListener('click', closeOverlays);
    o.dim.addEventListener('click', closeOverlays);
  };

  /* ---- mount: read line, scroll detection, nudge, ready ---- */
  useEffect(() => {
    const scroll = scrollRef.current!;
    const readLine = window.setTimeout(() => setReadLineTop(scroll.offsetTop + scroll.offsetHeight * 0.10), 0);
    /* scroll room: without it a short page could never drag its last verses up to the read line */
    spacerRef.current!.style.height = scroll.clientHeight + 'px';
    let detectTimer = 0;
    const onScroll = () => { clearTimeout(detectTimer); detectTimer = window.setTimeout(() => detectRef.current(), 90); };
    scroll.addEventListener('scroll', onScroll);
    const first = window.setTimeout(() => detectRef.current(), 60);
    const nudge = window.setInterval(() => {
      if (pending.current.size > 0 && !screenRef.current!.querySelector('.dim,.kscreen,.wcard')) {
        const c = counterRef.current!; c.classList.remove('nudge'); void c.offsetWidth; c.classList.add('nudge');
      }
    }, 30000);
    updateChips();
    onReady?.(handle());
    return () => {
      clearTimeout(readLine); clearTimeout(first); clearTimeout(detectTimer); clearInterval(nudge);
      scroll.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* a replaced page starts at the top and is measured afresh */
  useEffect(() => {
    if (!pageJustChanged.current) return;
    pageJustChanged.current = false;
    const frame = requestAnimationFrame(() => { scrollRef.current!.scrollTop = 0; setTimeout(() => detectRef.current(), 30); });
    return () => cancelAnimationFrame(frame);
  }, [page]);

  /* the finish box and the blurred taste beneath it size the scroll room so the box lands
     at the top of the reading area and nothing can carry the scroll past it (legacy renderFinishBox) */
  useLayoutEffect(() => {
    const box = finishRef.current, peek = peekRef.current, spacer = spacerRef.current, scroll = scrollRef.current;
    if (!box || !spacer || !scroll) return;
    if (peek) {
      peek.style.maxHeight = Math.max(120, scroll.clientHeight - box.offsetHeight) + 'px';
      peek.style.overflow = 'hidden';
    }
    spacer.style.height = '0px';
    const boxTopInScroll = box.getBoundingClientRect().top - scroll.getBoundingClientRect().top + scroll.scrollTop;
    let below = scroll.scrollHeight - boxTopInScroll;
    if (peek && below > scroll.clientHeight) {
      peek.style.maxHeight = Math.max(80, peek.offsetHeight - (below - scroll.clientHeight)) + 'px';
      below = scroll.scrollHeight - boxTopInScroll;
    }
    spacer.style.height = Math.max(0, scroll.clientHeight - below) + 'px';
    const complete = therm.total > 0 && therm.pct >= 100;
    if (complete && !wasComplete.current) {
      /* the page just finished — fetch the next page in the background now, so whichever button they tap responds straight away */
      if (host.signedIn()) host.prefetchNextPage();
      box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      host.tourPageComplete(handle());
    }
    wasComplete.current = complete;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipsTick]);

  const layout = prefs.layout === 'arabic' ? 'stack' : prefs.layout;
  const contentClass = ['content', 'layout-' + layout, prefs.translit ? '' : 'hide-tr',
    prefs.script === 'indopak' ? 'script-indopak' : '', prefs.script === 'turkish' ? 'script-turkish' : '',
    prefs.layout === 'arabic' ? 'layout-arabic-only' : ''].filter(Boolean).join(' ');
  const contentStyle = { '--arfs': prefs.arfs + 'px', '--enfs': prefs.enfs + 'px' } as CSSProperties;
  const complete = therm.total > 0 && therm.pct >= 100;
  const thermClass = 'counter therm' + (therm.pct >= 45 ? ' lit' : '') + (complete ? ' full' : '');
  const thermLabel = complete ? 'Finished' : therm.done + ' / ' + therm.total;
  const thermAria = complete ? 'Page read — tap to submit it' : therm.done + ' of ' + therm.total + ' verses read on this page';

  return (
    <div className={'screen' + (hideGuides ? ' hideguides' : '')} ref={screenRef}>
      <div className="app-header">
        <span className="back" style={{ cursor: 'pointer' }} role="button" aria-label={t('backLbl')} onClick={() => host.openLanding()}>‹</span>
        <span className="hdr-med" dangerouslySetInnerHTML={asHtml(icons.headerMedallion)} />
        <div className="ttl">
          <b dangerouslySetInnerHTML={asHtml(header.title)} />
          <small dangerouslySetInnerHTML={asHtml(header.sub)} />
          <small className="cause" dangerouslySetInnerHTML={asHtml(t('intentionLbl') + ': ' + t('causeName'))} />
        </div>
        <button className="hdr-search" aria-label={t('searchLbl')} title={t('searchLbl')} dangerouslySetInnerHTML={asHtml(icons.search)} onClick={() => host.searchOverlay(screenRef.current!)} />
        <button ref={settingsRef} className="hdr-ic" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px' }} aria-label={t('setT')} dangerouslySetInnerHTML={asHtml(icons.sliders)} onClick={openSettings} />
        <button
          ref={counterRef}
          className={thermClass}
          title="Your progress through this page"
          aria-label={thermAria}
          style={{ '--fill': therm.pct + '%' } as CSSProperties}
          onClick={submitOrNudge}
          dangerouslySetInnerHTML={asHtml(icons.birdMini + '<span>' + thermLabel + '</span>')}
        />
      </div>
      <div className="scrollarea" style={{ position: 'relative' }} ref={scrollRef}>
        <div className={contentClass} data-cust="1" style={contentStyle} ref={contentRef} onClick={onContentClick}>
          {built.items.map((item, i) => {
            if (item.kind === 'band') return <SurahBand key={'b' + i} surah={item.surah} host={host} />;
            if (item.kind === 'basmala') return <BasmalaRow key={'s' + i} host={host} />;
            return (
              <VerseRow
                key={item.verse.id}
                verse={item.verse}
                state={rowStates.get(item.verse.id) ?? null}
                current={currentId === item.verse.id}
                playing={playingId === item.verse.id}
                host={host}
              />
            );
          })}
          <div className={'finish' + (complete ? ' unlocked' : '')} data-page={page} ref={finishRef}>
            <b dangerouslySetInnerHTML={asHtml(t('finishQ', { n: page }))} />
            <div className="fbtns">
              <button className="btn solid" onClick={submitOrNudge} dangerouslySetInnerHTML={asHtml(t('btnSubmit'))} />
              <button ref={moreRef} className="btn outline" onClick={() => { void readMore(); }} dangerouslySetInnerHTML={asHtml(t('btnMore'))} />
            </div>
            <small className="fhint" dangerouslySetInnerHTML={asHtml(t('finishHint', { c: t('causeName') }))} />
          </div>
          {built.peek.length > 0 && (
            <div className="nextpeek" aria-hidden="true" ref={peekRef}>
              {built.peek.map((verse) => <VerseRow key={verse.id} verse={verse} state={null} current={false} playing={false} host={host} />)}
            </div>
          )}
          <div className="qcspacer" ref={spacerRef} />
        </div>
      </div>
      <div className="bottombar" ref={barRef}>
        <div className="barleft" />
        <div className="medallion" dangerouslySetInnerHTML={asHtml(icons.medallion(barPage))} />
        <div className="barright">
          <button className="playbtn" title={t('playLbl')} dangerouslySetInnerHTML={asHtml(icons.play)} onClick={startRecitation} />
        </div>
        {recitePrompt && <RecitationPrompt host={host} onChoose={openPlayer} />}
        {reciteQueue && <RecitationPlayer host={host} queue={reciteQueue} onVerse={onReciteVerse} onClose={() => setReciteQueue(null)} />}
      </div>
      <div className="readline" style={readLineTop == null ? undefined : { top: readLineTop + 'px' }}>
        <i dangerouslySetInnerHTML={asHtml(t('readLineLbl'))} />
      </div>
      <button className={'guidetoggle' + (hideGuides ? ' off' : '')} title={t('guidesToggle')} aria-label={t('guidesToggle')} dangerouslySetInnerHTML={asHtml(GUIDE_IC)} onClick={toggleGuides} />
    </div>
  );
}
